import { useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import {
  extractTagChartData,
  getKey,
  getTagsWithoutData,
} from '../SensorAnalysisView/Content/utils';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';

export const useTagData = () => {
  const selectedTags = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedTags
  );

  const tags = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedAsset?.device?.tags
  );

  const isLoadingData = useAppSelector(
    (state) => state.tagReducer.isLoadingData
  );
  const data = useAppSelector((state) => state.tagReducer.tagChartData);

  const activeButton = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.activeButton
  );

  const keyToUse = useMemo(() => getKey(activeButton), [activeButton]);

  const startDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.startDate
  );

  const endDate = useAppSelector(
    (state) => state.sensorAnalysisReducer.regularFilters.endDate
  );

  const selectedAsset = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedAsset
  );

  const dataType = useAppSelector(
    (state) => state.sensorAnalysisReducer.dataType
  );

  const tagGrainData = useAppSelector((state) => state.tagReducer.tagGrainData);

  const { unitSystem } = useCustomerInfo();

  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  const tagsWithoutData = useMemo(() => {
    if (!isLoadingData && data && data?.length > 0) {
      return getTagsWithoutData(
        data,
        selectedTags,
        keyToUse,
        activeButton,
        startDate,
        endDate,
        selectedAsset,
        unitSystem,
        dataType,
        tagGrainData,
        isLoadingData,
        timezoneBasedOnDisplayMode
      );
    }
    return [];
  }, [
    JSON.stringify(data),
    selectedTags,
    keyToUse,
    activeButton,
    startDate,
    endDate,
    selectedAsset,
    unitSystem,
    dataType,
    tagGrainData,
    isLoadingData,
    timezoneBasedOnDisplayMode,
  ]);

  const noDataTagAliases: string[] = useMemo(
    () =>
      tagsWithoutData
        .map((st) => {
          const mappedTag = tags?.find((item) => item.tagName === st);

          if (mappedTag) {
            return mappedTag.tagAlias;
          }
          return null;
        })
        .filter((alias): alias is string => Boolean(alias)),
    [tagsWithoutData, tags]
  );

  const sortedSelectedTags = useMemo(() => {
    return selectedTags
      .map((tag) => {
        const tagDetail = tags?.find((item) => item.tagName === tag);
        return tagDetail
          ? { id: tagDetail.tagName, alias: tagDetail.tagAlias }
          : null;
      })
      .filter((tg): tg is { id: string; alias: string } => tg !== null)
      .sort((a, b) => {
        const aInArr2 = noDataTagAliases.includes(a.alias);
        const bInArr2 = noDataTagAliases.includes(b.alias);
        if (aInArr2 && !bInArr2) return 1;
        if (bInArr2 && !aInArr2) return -1;
        return 0;
      })
      .map((item) => item.id);
  }, [selectedTags]);

  const tagAliases = useMemo(
    () =>
      tags?.length > 0
        ? selectedTags
            .map((st) => {
              const mappedTag = tags?.find((item) => item.tagName === st);
              if (mappedTag) {
                return mappedTag.tagAlias;
              }
              return st;
            })
            .filter((item) => item !== null)
            ?.sort((a, b) => {
              const aInArr2 = noDataTagAliases.includes(a);
              const bInArr2 = noDataTagAliases.includes(b);
              if (aInArr2 && !bInArr2) return 1;
              if (bInArr2 && !aInArr2) return -1;
              return 0;
            })
        : [],
    [selectedTags, tags]
  );

  // this returns data as well not only alias/ids of tags
  const selectedTagsWithData = useMemo(() => {
    return selectedTags
      .map((tag) => {
        const tagData = data.find((item: any) => item.tagName === tag);
        if (!tagData) return null;

        const { filteredArray, unit } = timezoneBasedOnDisplayMode
          ? extractTagChartData(
              tagData,
              keyToUse,
              activeButton,
              startDate,
              endDate,
              selectedAsset,
              unitSystem,
              dataType,
              tagGrainData,
              tag,
              timezoneBasedOnDisplayMode
            )
          : { filteredArray: [], unit: '' };

        if (Array.isArray(filteredArray) && filteredArray.length > 0) {
          return {
            name: tag,
            data: filteredArray,
            unit,
          };
        }

        return null;
      })
      .filter(
        (tg): tg is { name: string; data: any[]; unit: string } => tg !== null
      );
  }, [
    selectedTags,
    data,
    keyToUse,
    activeButton,
    startDate,
    endDate,
    selectedAsset,
    unitSystem,
    dataType,
    tagGrainData,
    timezoneBasedOnDisplayMode,
  ]);

  return {
    sortedSelectedTags,
    tagsWithoutData,
    noDataTagAliases,
    tagAliases,
    selectedTagsWithData,
  };
};
